import { Pencil, Check } from "lucide-react";
import img from '../imgs/Trainer Image.png';
import { UserInfoData } from "../types/userInfo";
import { useState, useEffect, useRef } from "react";
import { Config } from "../API/config";
import axios from "axios";
import toast from "react-hot-toast";
import { MdAddCircle } from "react-icons/md";

interface SubCategory {
    _id: string;
    name: string;
    categoryId: string;
    createdAt: string;
    updatedAt: string;
    __v: number;
}

interface Category {
    _id: string;
    name: string;
    subCategories: SubCategory[];
}

const TrainerViewCard = ({ id }: { id: string }) => {
    const [user, setUser] = useState<UserInfoData | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [categories, setCategories] = useState<Category[]>([]);
    const [error, setError] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [img, setImg] = useState<File | null>(null);
    const [updatedTrainer, setUpdatedTrainer] = useState({
        name: "",
        email: "",
        phone: "",
        profileImage: "",
        gender: "",
        governorate: "",
        city: "",
        yearsOfExperience: 0,
        age: 0,
        numberOfLessonsOnWeek: 0,
        priceOfLesson: 0,
        needs: "",
        serviceGovernorates: [] as string[],
        serviceCities: [] as string[],
        category: "",
        subCategory: "",
        notice: "",
        type: "",
    });

    // Fetch categories on component mount
    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const { data } = await axios.get(Config.getAllRegisterCategory, {
                    withCredentials: true,
                });
                if (data.statusCode === 200) {
                    setCategories(data.data);
                } else {
                    setError("Failed to load categories");
                }
            } catch (err) {
                console.error("Error fetching categories:", err);
                setError("Failed to load categories");
            }
        };

        fetchCategories();
    }, []);

    const fetchTrainerData = async () => {
        try {
            setLoading(true);
            const { data } = await axios.get(`${Config.getUserInfoById}/${id}`, {
                withCredentials: true,
            });

            if (data) {
                setUser(data);
                setUpdatedTrainer({
                    name: data.name || "",
                    email: data.email || "",
                    profileImage: data.profileImage || "",
                    phone: data.phone || "",
                    gender: data.gender || "",
                    governorate: data.governorate || "",
                    city: data.city || "",
                    yearsOfExperience: data.trainerId?.yearsOfExperience || 0,
                    age: data.trainerId?.age || 0,
                    numberOfLessonsOnWeek: data.trainerId?.numberOfLessonsOnWeek || 0,
                    priceOfLesson: data.trainerId?.priceOfLesson || 0,
                    needs: data.trainerId?.needs || "",
                    serviceGovernorates: data.trainerId?.serviceGovernorates || [],
                    serviceCities: data.trainerId?.serviceCities || [],
                    category: data.trainerId?.category || "",
                    subCategory: data.trainerId?.subCategory || "",
                    notice: data.trainerId?.notice || "",
                    type: data.trainerId?.type || "",
                });
            } else {
                console.error("No data returned from API");
            }
        } catch (err) {
            console.error("Error fetching trainer data:", err);
            toast.error("فشل في جلب بيانات المدرب");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (id) {
            fetchTrainerData();
        }
    }, [id]);

    const handleEditClick = () => {
        setIsEditing(true);
    };

    const handleInputChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
    ) => {
        const { name, value } = e.target;
        setUpdatedTrainer((prev) => {
            // Reset subCategory when category changes
            if (name === "category") {
                return {
                    ...prev,
                    category: value,
                    subCategory: "", // Reset subCategory when category changes
                };
            }
            return {
                ...prev,
                [name]:
                    name === "yearsOfExperience" ||
                        name === "age" ||
                        name === "numberOfLessonsOnWeek" ||
                        name === "priceOfLesson"
                        ? Number(value) || 0
                        : value,
            };
        });
    };

    const handleArrayInputChange = (
        e: React.ChangeEvent<HTMLInputElement>,
        field: "serviceGovernorates" | "serviceCities"
    ) => {
        const value = e.target.value.split(",").map((item) => item.trim());
        setUpdatedTrainer((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleUpdateTrainer = async () => {
        try {
            setLoading(true);
            const { profileImage, ...trainerDataWithoutImage } = updatedTrainer;

            const { data } = await axios.patch(
                `${Config.editTrainer}/${id}`,
                trainerDataWithoutImage,
                { withCredentials: true }
            );

            if (data.statusCode === 200) {
                setUser(data.data); 
                setIsEditing(false);
                toast.success("تم تحديث بيانات المدرب بنجاح");
                fetchTrainerData();
            }
        } catch (err) {
            console.error("Error updating trainer data:", err);
            toast.error("فشل في تحديث بيانات المدرب");
        } finally {
            setLoading(false);
        }
    };
    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        try {
            if (!e.target.files || !e.target.files[0]) return;

            setLoading(true);
            const file = e.target.files[0];
            setImg(file);

            const formData = new FormData();
            formData.append('profileImage', file);

            const { data } = await axios.post(
                `${Config.updateTrainerProfileImage}?id=${id}`,
                formData,
                {
                    withCredentials: true,
                    headers: { 'Content-Type': 'multipart/form-data' }
                }
            );


            if (data.statusCode === 201) {
                setIsEditing(false);
                toast.success("تم تحديث الصورة الشخصية بنجاح");
            }
        } catch (err) {
            console.error("Error updating profile image:", err);
        } finally {
            setLoading(false);
        }
    };
    // Get subcategories for the selected category
    const selectedCategory = categories.find(
        (cat) => cat.name === updatedTrainer.category
    );
    const subCategories = selectedCategory ? selectedCategory.subCategories : [];

    return (
        <div className="p-6 shadow-lg rounded-xl border border-gray-200 bg-white text-right">
            {loading ? (
                <p className="text-center text-gray-600">جارٍ التحميل...</p>
            ) : error ? (
                <p className="text-center text-red-600">{error}</p>
            ) : (
                <>
                    <div className="flex items-center space-x-4 rtl:space-x-reverse">
                        {isEditing ? (
                            <div className="mt-10 relative group overflow-hidden rounded-full">
                                <img
                                    src={(img ? URL.createObjectURL(img) : updatedTrainer.profileImage) as string}
                                    className="w-32 h-16 rounded-full border border-gray-300 relative cursor-pointer z-10"
                                    alt="Profile"
                                />
                                <div
                                    onClick={() => fileInputRef.current?.click()}
                                    className="absolute inset-0 bg-black opacity-0 group-hover:opacity-50 transition-opacity duration-300 rounded-full flex items-center justify-center cursor-pointer z-20"
                                >
                                    <MdAddCircle className="fill-white w-[30%] h-[30%]" />
                                    <input
                                        type="file"
                                        className="hidden"
                                        ref={fileInputRef}
                                        onChange={handleFileChange}
                                    />
                                </div>
                            </div>
                        ) : (
                            <img
                                src={updatedTrainer?.profileImage}
                                alt={user?.name || 'Trainer'}
                                className="w-12 h-12 rounded-full border border-gray-300"
                            />
                        )}
                        <div>
                            {isEditing ? (
                                <input
                                    type="text"
                                    name="name"
                                    value={updatedTrainer.name}
                                    onChange={handleInputChange}
                                    className="text-lg font-semibold border rounded p-1 w-3/4"
                                />
                            ) : (
                                <h2 className="text-lg font-semibold">{user?.name}</h2>
                            )}
                            {isEditing ? (
                                <select
                                    name="category"
                                    value={updatedTrainer.category}
                                    onChange={handleInputChange}
                                    className="text-gray-500 border rounded p-1 w-3/4 mt-1"
                                >
                                    <option value="">اختر الفئة</option>
                                    {categories.map((cat) => (
                                        <option key={cat._id} value={cat.name}>
                                            {cat.name}
                                        </option>
                                    ))}
                                </select>
                            ) : (
                                <p className="text-gray-500">{user?.trainerId?.category}</p>
                            )}
                        </div>
                    </div>
                    <div className="mt-4 space-y-2">
                        <div className="grid grid-cols-2 gap-4 text-sm text-gray-700">
                            <p>
                                <span className="font-semibold">المنطقة:</span>
                                {isEditing ? (
                                    <input
                                        type="text"
                                        name="city"
                                        value={updatedTrainer.city}
                                        onChange={handleInputChange}
                                        className="border rounded p-1 w-full"
                                    />
                                ) : (
                                    user?.city
                                )}
                            </p>
                            <p>
                                <span className="font-semibold">عدد الحصص:</span>
                                {isEditing ? (
                                    <input
                                        type="number"
                                        name="numberOfLessonsOnWeek"
                                        value={updatedTrainer.numberOfLessonsOnWeek}
                                        onChange={handleInputChange}
                                        className="border rounded p-1 w-full"
                                    />
                                ) : (
                                    `${user?.trainerId?.numberOfLessonsOnWeek} حصص / أسبوع`
                                )}
                            </p>
                            <p>
                                <span className="font-semibold">السن:</span>
                                {isEditing ? (
                                    <input
                                        type="number"
                                        name="age"
                                        value={updatedTrainer.age}
                                        onChange={handleInputChange}
                                        className="border rounded p-1 w-full"
                                    />
                                ) : (
                                    `${user?.trainerId?.age} سنة`
                                )}
                            </p>
                            <p>
                                <span className="font-semibold">رقم التليفون:</span>
                                {isEditing ? (
                                    <input
                                        type="text"
                                        name="phone"
                                        value={updatedTrainer.phone}
                                        onChange={handleInputChange}
                                        className="border rounded p-1 w-full"
                                    />
                                ) : (
                                    user?.phone
                                )}
                            </p>
                            <p>
                                <span className="font-semibold">المحافظة:</span>
                                {isEditing ? (
                                    <input
                                        type="text"
                                        name="governorate"
                                        value={updatedTrainer.governorate}
                                        onChange={handleInputChange}
                                        className="border rounded p-1 w-full"
                                    />
                                ) : (
                                    user?.governorate
                                )}
                            </p>
                            <p>
                                <span className="font-semibold">النوع:</span>
                                {isEditing ? (
                                    <input
                                        type="text"
                                        name="gender"
                                        value={updatedTrainer.gender}
                                        onChange={handleInputChange}
                                        className="border rounded p-1 w-full"
                                    />
                                ) : (
                                    user?.gender
                                )}
                            </p>
                            <p>
                                <span className="font-semibold">البريد الإلكتروني:</span>
                                {isEditing ? (
                                    <input
                                        type="email"
                                        name="email"
                                        value={updatedTrainer.email}
                                        onChange={handleInputChange}
                                        className="border rounded p-1 w-full"
                                    />
                                ) : (
                                    user?.email
                                )}
                            </p>
                            <p>
                                <span className="font-semibold">سنوات الخبرة:</span>
                                {isEditing ? (
                                    <input
                                        type="number"
                                        name="yearsOfExperience"
                                        value={updatedTrainer.yearsOfExperience}
                                        onChange={handleInputChange}
                                        className="border rounded p-1 w-full"
                                    />
                                ) : (
                                    `${user?.trainerId?.yearsOfExperience} سنوات`
                                )}
                            </p>
                            <p className="col-span-2">
                                <span className="font-semibold">الفئة الفرعية:</span>
                                {isEditing ? (
                                    <select
                                        name="subCategory"
                                        value={updatedTrainer.subCategory}
                                        onChange={handleInputChange}
                                        className="border rounded p-1 w-full"
                                        disabled={!updatedTrainer.category}
                                    >
                                        <option value="">اختر الفئة الفرعية</option>
                                        {subCategories.map((subCat) => (
                                            <option key={subCat._id} value={subCat.name}>
                                                {subCat.name}
                                            </option>
                                        ))}
                                    </select>
                                ) : (
                                    user?.trainerId?.subCategory
                                )}
                            </p>
                        </div>
                        {isEditing ? (
                            <textarea
                                name="needs"
                                value={updatedTrainer.needs}
                                onChange={handleInputChange}
                                className="text-gray-600 mt-4 border rounded p-2 w-full"
                                rows={4}
                            />
                        ) : (
                            <p className="text-gray-600 mt-4">{user?.trainerId?.needs}</p>
                        )}
                        <div className="mt-4">
                            <span className="font-semibold">المحافظات المخدومة:</span>
                            {isEditing ? (
                                <input
                                    type="text"
                                    name="serviceGovernorates"
                                    value={updatedTrainer.serviceGovernorates.join(", ")}
                                    onChange={(e) => handleArrayInputChange(e, "serviceGovernorates")}
                                    className="border rounded p-1 w-full"
                                    placeholder="ادخل المحافظات مفصولة بفواصل"
                                />
                            ) : (
                                <p className="text-gray-600">
                                    {user?.trainerId?.serviceGovernorates?.join(", ")}
                                </p>
                            )}
                        </div>
                        <div className="mt-4">
                            <span className="font-semibold">المدن المخدومة:</span>
                            {isEditing ? (
                                <input
                                    type="text"
                                    name="serviceCities"
                                    value={updatedTrainer.serviceCities.join(", ")}
                                    onChange={(e) => handleArrayInputChange(e, "serviceCities")}
                                    className="border rounded p-1 w-full"
                                    placeholder="ادخل المدن مفصولة بفواصل"
                                />
                            ) : (
                                <p className="text-gray-600">
                                    {user?.trainerId?.serviceCities?.join(", ")}
                                </p>
                            )}
                        </div>
                        <div className="mt-4">
                            <span className="font-semibold">ملاحظات:</span>
                            {isEditing ? (
                                <textarea
                                    name="notice"
                                    value={updatedTrainer.notice}
                                    onChange={handleInputChange}
                                    className="border rounded p-2 w-full"
                                    rows={2}
                                />
                            ) : (
                                <p className="text-gray-600">{user?.trainerId?.notice}</p>
                            )}
                        </div>
                        <div className="flex justify-end items-center bg-transparent text-green-700 p-3 rounded-md mt-4">
                            {isEditing ? (
                                <input
                                    type="number"
                                    name="priceOfLesson"
                                    value={updatedTrainer.priceOfLesson}
                                    onChange={handleInputChange}
                                    className="border rounded p-1 font-semibold text-green-700"
                                />
                            ) : (
                                <span className="font-semibold">
                                    {user?.trainerId?.priceOfLesson} ج.م / حصة
                                </span>
                            )}
                        </div>
                        <div className="flex justify-end mt-4 gap-3">
                            {isEditing ? (
                                <>
                                    <button
                                        onClick={handleUpdateTrainer}
                                        className="bg-green-500 text-white px-4 py-2 rounded-lg flex items-center gap-2 rtl:flex-row-reverse"
                                    >
                                        <Check size={16} /> حفظ
                                    </button>
                                    <button
                                        onClick={() => setIsEditing(false)}
                                        className="border border-gray-300 px-4 py-2 rounded-lg"
                                    >
                                        إلغاء
                                    </button>
                                </>
                            ) : (
                                <button
                                    onClick={handleEditClick}
                                    className="border border-gray-300 px-4 py-2 rounded-lg flex items-center gap-2 rtl:flex-row-reverse"
                                >
                                    <Pencil size={16} /> تعديل
                                </button>
                            )}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default TrainerViewCard;